<template>
  <div class="w-full mb-3" v-click-outside="closeDropdown">
    <label :for="inputId" class="text-sm font-medium text-black">
      {{ label }}
    </label>

    <div class="flex w-full gap-2 mt-2">
      <div class="relative shrink-0">
        <button
          type="button"
          aria-label="Country calling code"
          class="flex h-full w-24 cursor-pointer items-center gap-1.5 rounded-md border bg-white px-3 py-2 text-sm text-gray-700 outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
          :class="
            dropdownOpen ? 'border-blue-600 ring-1 ring-blue-600' : errorMessage ? 'border-red-500' : 'border-gray-300'
          "
          :disabled="disabled"
          @click="toggleDropdown"
        >
          <span>{{ selectedCountryData?.flag }}</span>
          <span>{{ selectedCountryData?.dialCode }}</span>
          <span class="ml-auto text-xs text-gray-400">▼</span>
        </button>

        <div
          v-if="dropdownOpen"
          class="absolute left-0 top-full z-50 mt-1 w-40 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          <button
            v-for="country in countries"
            :key="country.code"
            type="button"
            class="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-800 transition-colors hover:bg-gray-100"
            :class="selectedCountry === country.code ? 'bg-blue-50 font-medium text-blue-700' : ''"
            @click="selectCountry(country.code)"
          >
            <span v-if="selectedCountry === country.code" class="text-blue-600">✓</span>
            <span v-else class="w-3"></span>
            <span>{{ country.flag }}</span>
            <span>{{ country.dialCode }}</span>
          </button>
        </div>
      </div>

      <input
        :id="inputId"
        :value="mobileNumber"
        type="tel"
        inputmode="tel"
        autocomplete="tel-national"
        :required="required"
        :placeholder="placeholder"
        class="min-w-0 flex-1 rounded-md border bg-white px-3 py-2 text-sm text-gray-700 outline-none placeholder:text-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
        :class="
          errorMessage ? 'border-red-500' : isFocused ? 'border-blue-600 ring-1 ring-blue-600' : 'border-gray-300'
        "
        data-testid="mobile"
        @input="handleInput"
        @blur="
          isFocused = false;
          validateMobile();
        "
        :disabled="disabled"
        @focus="isFocused = true"
      />
    </div>

    <p v-if="errorMessage" class="mt-1 text-sm text-red-600" role="alert">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { useGeneralStore } from '@/stores/general';
import { getMobileValidation, parseMobile, type SmsOriginCountry } from '@/utils/validation';
import type { CountryCode } from 'libphonenumber-js';
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: 'Mobile number' },
  placeholder: { type: String, default: 'Enter mobile number' },
  required: { type: Boolean, default: true },
  id: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const generalStore = useGeneralStore();

const inputId = computed(() => props.id || 'mobile-number');
const selectedCountry = ref<CountryCode>('ZA');
const mobileNumber = ref('');
const errorMessage = ref('');
const dropdownOpen = ref(false);
const isFocused = ref(false);
const isUserTyping = ref(false);
const countries = computed(() => generalStore.smsOriginCountries);
const allowedCountries = computed(() => countries.value.map((c) => c.code));

const selectedCountryData = computed(() => countries.value.find((c) => c.code === selectedCountry.value));

onMounted(() => generalStore.loadSmsOriginCountries());

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value;
  isFocused.value = true;
};

const closeDropdown = () => {
  dropdownOpen.value = false;
};

const selectCountry = (code: CountryCode) => {
  selectedCountry.value = code;
  dropdownOpen.value = false;
};

const vClickOutside = {
  mounted(el: any, binding: any) {
    el._clickOutside = (e: any) => {
      if (!el.contains(e.target)) binding.value(e);
    };
    // defer so the opening click doesn't immediately trigger close
    setTimeout(() => document.addEventListener('click', el._clickOutside), 0);
  },
  unmounted(el: any) {
    document.removeEventListener('click', el._clickOutside);
  },
};

watch(
  () => props.modelValue,
  (value) => {
    if (isUserTyping.value) return;
    if (!value) return;
    const country = countries.value.find((item) => value.startsWith(item.dialCode));
    if (country) {
      selectedCountry.value = country.code;
      const nationalNumber = value.slice(country.dialCode.length);
      mobileNumber.value = nationalNumber.startsWith('0') ? nationalNumber.slice(1) : nationalNumber;
      return;
    }
    mobileNumber.value = value;
  },
  { immediate: true },
);

watch(selectedCountry, () => {
  errorMessage.value = '';
  if (mobileNumber.value) validateMobile();
});

const handleInput = (event: Event) => {
  isUserTyping.value = true;
  mobileNumber.value = (event.target as HTMLInputElement).value;
  errorMessage.value = '';
  if (!mobileNumber.value) {
    emit('update:modelValue', '');
    isUserTyping.value = false;
    return;
  }
  const validation = getMobileValidation(mobileNumber.value, selectedCountry.value, allowedCountries.value);
  if (!validation.incomplete && !validation.valid) errorMessage.value = validation.error;
  emit('update:modelValue', validation.valid ? parseMobile(mobileNumber.value, selectedCountry.value) : '');
  isUserTyping.value = false;
};

const validateMobile = () => {
  if (!mobileNumber.value) {
    errorMessage.value = props.required ? 'Mobile number is required' : '';
    emit('update:modelValue', '');
    return;
  }
  const validation = getMobileValidation(mobileNumber.value, selectedCountry.value, allowedCountries.value);
  if (!validation.valid) {
    errorMessage.value = validation.error;
    emit('update:modelValue', '');
    return;
  }
  errorMessage.value = '';
  emit('update:modelValue', parseMobile(mobileNumber.value, selectedCountry.value));
};
</script>
